import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getAuthToken } from "@/lib/api";

const ThemeCtx = createContext(null);
export const useTheme = () => useContext(ThemeCtx);

const DEFAULTS = { theme: "dark", text_size: "standard", reduced_motion: false, sound_feedback: true, default_landing: "home", notifications: { equipment: true, qc: true, approvals: true } };

function apply({ theme, text_size, reduced_motion }) {
  const root = document.documentElement;
  let resolved = theme;
  if (theme === "system") {
    resolved = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  root.setAttribute("data-theme", resolved);
  root.setAttribute("data-text", text_size === "large" ? "large" : "standard");
  root.setAttribute("data-motion", reduced_motion ? "reduced" : "normal");
}

export function ThemeProvider({ children }) {
  const [prefs, setPrefs] = useState(() => {
    try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("sg_prefs") || "{}") }; }
    catch { return DEFAULTS; }
  });
  // Captured before the apply-effect writes sg_prefs — true only on a device with no saved prefs.
  const [hadLocal] = useState(() => !!localStorage.getItem("sg_prefs"));

  // Apply immediately + react to system theme changes when in system mode.
  useEffect(() => {
    apply(prefs);
    localStorage.setItem("sg_prefs", JSON.stringify(prefs));
    if (prefs.theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => apply(prefs);
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, [prefs]);

  // Sync from server only on a fresh device (no local prefs) so we never clobber a just-changed local setting.
  useEffect(() => {
    if (!getAuthToken() || hadLocal) return;
    api.get("/me/preferences").then(({ data }) => {
      setPrefs((p) => ({ ...p, ...data }));
    }).catch(() => {});
  }, []);

  const update = useCallback((partial) => {
    setPrefs((p) => {
      const next = { ...p, ...partial };
      localStorage.setItem("sg_prefs", JSON.stringify(next));
      if (getAuthToken()) api.put("/me/preferences", next).catch(() => {});
      return next;
    });
  }, []);

  const syncFromServer = useCallback(async () => {
    try { const { data } = await api.get("/me/preferences"); setPrefs((p) => ({ ...p, ...data })); } catch {}
  }, []);

  return <ThemeCtx.Provider value={{ prefs, update, syncFromServer }}>{children}</ThemeCtx.Provider>;
}
